import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  CircularProgress,
  useTheme,
  alpha,
  Box,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormField from './FormField';

const QualificationForm = ({
  formData,
  errors,
  touched,
  loading,
  handleChange,
  handleQualificationSubmit,
  hasError,
  getErrorMessage,
}) => {
  const theme = useTheme();

  return (
    <Paper 
      elevation={8} 
      sx={{ 
        p: { xs: 3, md: 5 }, 
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.02)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
      }}
    >
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 700, 
          mb: 4,
          textAlign: "center",
          background: "linear-gradient(45deg, #fff, #64B5F6)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Agent Qualification Form
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          textAlign: "center",
          mb: 4,
          color: alpha(theme.palette.text.secondary, 0.9),
        }}
      >
        Please fill out this form to help us understand your needs and recommend the best plan for you.
      </Typography>

      <form onSubmit={handleQualificationSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <FormField 
              label="What type of insurance are you currently selling?" 
              required
              error={hasError("insuranceType")}
              helperText={getErrorMessage("insuranceType")}
            >
              <FormControl fullWidth variant="outlined" error={hasError("insuranceType")}>
                <Select
                  name="insuranceType"
                  value={formData.insuranceType}
                  onChange={handleChange}
                  displayEmpty
                  required
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.1),
                    backdropFilter: "blur(10px)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.text.primary, 0.1),
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select One</MenuItem>
                  <MenuItem value="finalExpense">Final Expense</MenuItem>
                  <MenuItem value="Term Life">Term Life</MenuItem>
                  <MenuItem value="Health Insurance/Medicare">Health Insurance/Medicare 
                  </MenuItem>
                  <MenuItem value="iul">IUL</MenuItem>
                  <MenuItem value="Annuity">Annuity</MenuItem>
                </Select>
              </FormControl>
            </FormField>

            <FormField 
              label="Have you sold above $10,000 AP of Life Insurance before?" 
              required
              error={hasError("experience")}
              helperText={getErrorMessage("experience")}
            >
              <RadioGroup
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                row
              >
                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FormField>

            <FormField 
              label="Have you taken live transfers before?" 
              required
              error={hasError("transfers")}
              helperText={getErrorMessage("transfers")}
            >
              <RadioGroup
                name="transfers"
                value={formData.transfers}
                onChange={handleChange}
                row
              >
                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FormField>

            <FormField 
              label="How many leads do you purchase weekly?" 
              required
              error={hasError("weeklyLeads")}
              helperText={getErrorMessage("weeklyLeads")}
            >
              <FormControl fullWidth variant="outlined" error={hasError("weeklyLeads")}>
                <Select
                  name="weeklyLeads"
                  value={formData.weeklyLeads}
                  onChange={handleChange}
                  displayEmpty
                  required
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.1),
                    backdropFilter: "blur(10px)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: hasError("weeklyLeads") 
                        ? theme.palette.error.main 
                        : alpha(theme.palette.text.primary, 0.1),
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: hasError("weeklyLeads")
                        ? theme.palette.error.main
                        : alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select One</MenuItem>
                  <MenuItem value="30">30</MenuItem>
                  <MenuItem value="60">60</MenuItem>
                  <MenuItem value="100">100</MenuItem>
                </Select>
              </FormControl>
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField 
              label="How much annual premium (AP) would you like to write monthly?" 
              required
              error={hasError("apGoal")}
              helperText={getErrorMessage("apGoal")}
            >
              <FormControl fullWidth variant="outlined" error={hasError("apGoal")}>
                <Select
                  name="apGoal"
                  value={formData.apGoal}
                  onChange={handleChange}
                  displayEmpty
                  required
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.1),
                    backdropFilter: "blur(10px)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: hasError("apGoal") 
                        ? theme.palette.error.main 
                        : alpha(theme.palette.text.primary, 0.1),
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: hasError("apGoal")
                        ? theme.palette.error.main
                        : alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select One</MenuItem>
                  <MenuItem value="10-20k">$10,000-$20,000 Monthly</MenuItem>
                  <MenuItem value="20-30k">$20,000-$30,000 Monthly</MenuItem>
                  <MenuItem value="30-50k">$30,000-$50,000 Monthly</MenuItem>
                </Select>
              </FormControl>
            </FormField>

            <FormField 
              label="Do you currently utilize a CRM?" 
              required
              error={hasError("crm")}
              helperText={getErrorMessage("crm")}
            >
              <RadioGroup
                name="crm"
                value={formData.crm}
                onChange={handleChange}
                row
              >
                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FormField>

            <FormField 
              label="What is your means of facilitating business?" 
              required
              error={hasError("method")}
              helperText={getErrorMessage("method")}
            >
              <RadioGroup
                name="method"
                value={formData.method}
                onChange={handleChange}
                row
              >
                <FormControlLabel value="in-person" control={<Radio color="primary" />} label="In Person" />
                <FormControlLabel value="telesales" control={<Radio color="primary" />} label="Telesales" />
              </RadioGroup>
            </FormField>

            <FormField 
              label="Are you ready for AI-driven vetted leads?" 
              required
              error={hasError("ready")}
              helperText={getErrorMessage("ready")}
            >
              <RadioGroup
                name="ready"
                value={formData.ready}
                onChange={handleChange}
              >
                <FormControlLabel 
                  value="yes" 
                  control={<Radio color="primary" />} 
                  label="Yes, I want vetted leads ready for my call" 
                />
                <FormControlLabel 
                  value="no" 
                  control={<Radio color="primary" />} 
                  label="No, I'm not ready to scale (Leave Page)" 
                />
              </RadioGroup>
            </FormField>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox 
                  name="terms" 
                  checked={formData.terms} 
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>
                    I understand this system requires CRM proficiency and readiness for high lead flow
                  </Typography>
                  <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                </Box>
              }
              sx={{ mb: 1 }}
            />
            {hasError("terms") && ( 
              <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5, mb: 2 }}>
                {getErrorMessage("terms")}
              </Typography>
            )}

            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              size="large"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={24} /> : <ArrowForwardIcon />}
              sx={{
                py: 2,
                background: "linear-gradient(45deg, #1976D2, #64B5F6)",
                borderRadius: 2,
                fontWeight: 600,
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(25, 118, 210, 0.4)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
                  transition: "all 0.6s ease",
                },
                "&:hover": {
                  boxShadow: "0 6px 25px rgba(25, 118, 210, 0.6)",
                  "&::before": {
                    left: "100%",
                  },
                },
              }}
            >
              {loading ? "Processing..." : "Continue to Select Your Plan"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default QualificationForm; 